import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatEUR } from "@/lib/api";
import { Users, Receipt, ShieldCheck, CalendarClock, HardDrive } from "lucide-react";

const Stat = ({ label, value, icon: Icon, testid, hint, alert }) => (
  <div data-testid={testid} className={`border rounded-md p-6 ${alert ? "border-amber-300 bg-amber-50" : "bg-white border-velta-border"}`}>
    <div className="flex items-start justify-between mb-4">
      <div className="text-xs font-mono uppercase tracking-widest text-velta-mute">{label}</div>
      <Icon className="w-5 h-5 text-velta-subtle" strokeWidth={1.5} />
    </div>
    <div className="font-mono text-4xl font-bold tracking-tighter">{value}</div>
    {hint ? <div className="text-xs text-velta-mute mt-2">{hint}</div> : null}
  </div>
);

const ACTIVITY_LABELS = {
  negozio_animali: "Negozio Animali",
  toelettatura: "Toelettatura",
  entrambe: "Negozio + Toelettatura",
};

const daysBadge = (d) => {
  if (d < 0) return { label: `Scaduto da ${Math.abs(d)}g`, cls: "bg-red-600 text-white" };
  if (d === 0) return { label: "Oggi", cls: "bg-red-600 text-white" };
  if (d <= 7) return { label: `${d}g`, cls: "bg-red-100 text-red-700 border border-red-300" };
  if (d <= 15) return { label: `${d}g`, cls: "bg-amber-100 text-amber-700 border border-amber-300" };
  return { label: `${d}g`, cls: "bg-velta-bg text-velta-ink border border-velta-border" };
};

export default function AdminHome() {
  const [stats, setStats] = useState(null);
  const [disk, setDisk] = useState(null);
  const [expiring, setExpiring] = useState(null);

  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data));
    api.get("/admin/clients/expiring?days=30").then((r) => setExpiring(r.data || [])).catch(() => setExpiring([]));
  }, []);

  useEffect(() => {
    let stop = false;
    const load = () => {
      api.get("/admin/disk").then((r) => { if (!stop) setDisk(r.data); }).catch(() => {});
    };
    load();
    const timer = setInterval(load, 30000);
    return () => { stop = true; clearInterval(timer); };
  }, []);

  return (
    <div className="p-8 lg:p-12 max-w-6xl">
      <div className="text-xs font-mono uppercase tracking-[0.25em] text-velta-mute mb-3">Panoramica</div>
      <h1 className="font-heading text-4xl font-bold tracking-tight mb-2">Sistema VELTA</h1>
      <p className="text-velta-mute mb-10">Stato della rete clienti.</p>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
        {stats && (
          <>
            <Stat testid="admin-stat-clients" label="Clienti attivi" value={stats.active_clients} icon={Users} />
            <Stat testid="admin-stat-licenses-used" label="Contratti attivi" value={stats.used_licenses} icon={ShieldCheck} />
            <Stat testid="admin-stat-orders" label="Scontrini totali" value={stats.total_orders} icon={Receipt} />
          </>
        )}
        <Stat
          testid="admin-disk-meter"
          label="Disco libero"
          value={disk ? `${disk.disk_free_pct}%` : "…"}
          icon={HardDrive}
          alert={!!disk?.disk_alert}
          hint={disk
            ? (disk.disk_alert
              ? `${disk.disk_free_gb} GB su ${disk.disk_total_gb} GB. Allarga il volume su Hetzner.`
              : `${disk.disk_free_gb} GB su ${disk.disk_total_gb} GB · si aggiorna ogni 30 secondi`)
            : "Lettura dal server…"}
        />
      </div>

      <div data-testid="admin-expiring-widget" className="bg-white border border-velta-border rounded-md p-6 mb-10">
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-heading text-xl font-semibold flex items-center gap-2">
            <CalendarClock className="w-5 h-5 text-[#2D5594]" /> Contratti in scadenza
            <span className="text-xs font-mono font-normal text-velta-mute">(prossimi 30 giorni)</span>
          </h3>
          <Link to="/admin/clienti" data-testid="admin-expiring-goto-clients" className="text-sm underline text-velta-ink hover:text-[#2D5594]">
            Gestisci clienti →
          </Link>
        </div>
        {expiring === null && <p className="text-sm text-velta-mute">Caricamento…</p>}
        {expiring !== null && expiring.length === 0 && (
          <p className="text-sm text-velta-mute" data-testid="admin-expiring-empty">
            Nessun contratto in scadenza nei prossimi 30 giorni. Tutto sotto controllo.
          </p>
        )}
        {expiring !== null && expiring.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] font-mono uppercase tracking-widest text-velta-mute border-b border-velta-border">
                  <th className="pb-2 pr-4">Cliente</th>
                  <th className="pb-2 pr-4">Attività</th>
                  <th className="pb-2 pr-4">Scadenza</th>
                  <th className="pb-2 pr-4">Giorni</th>
                  <th className="pb-2 pr-4">Canone</th>
                  <th className="pb-2">Pagamento</th>
                </tr>
              </thead>
              <tbody>
                {(Array.isArray(expiring) ? expiring : []).map((c) => {
                  const b = daysBadge(c.days_left);
                  return (
                    <tr key={c.tenant_id} data-testid={`admin-expiring-row-${c.tenant_id}`} className="border-b border-velta-border/60 last:border-0">
                      <td className="py-3 pr-4">
                        <div className="font-medium">{c.business_name}</div>
                        <div className="text-[11px] text-velta-mute">{c.owner_email || c.phone || ""}</div>
                      </td>
                      <td className="py-3 pr-4">
                        <div className="text-xs font-medium">{ACTIVITY_LABELS[c.activity_type] || "—"}</div>
                      </td>
                      <td className="py-3 pr-4 font-mono text-xs">{c.next_due_date}</td>
                      <td className="py-3 pr-4">
                        <span className={`text-[10px] font-mono font-bold px-2 py-1 rounded ${b.cls}`}>{b.label}</span>
                      </td>
                      <td className="py-3 pr-4 font-mono">
                        {c.agreed_amount_eur != null ? formatEUR(c.agreed_amount_eur) : "—"}
                        {c.billing_cycle && <span className="text-[10px] text-velta-mute">/{c.billing_cycle === "annuale" ? "anno" : "mese"}</span>}
                      </td>
                      <td className="py-3">
                        <span className={`text-[10px] font-mono uppercase px-2 py-1 rounded ${
                          c.payment_status === "pagato" ? "bg-emerald-100 text-emerald-700" :
                          c.payment_status === "in_ritardo" ? "bg-red-100 text-red-700" :
                          "bg-velta-bg text-velta-mute"
                        }`}>{c.payment_status || "—"}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {stats && (
        <div className="bg-white border border-velta-border rounded-md p-6">
          <h3 className="font-heading text-xl font-semibold mb-5">Clienti per modalità</h3>
          <div className="grid sm:grid-cols-4 gap-4">
            {Object.entries(stats.by_mode || {}).map(([mode, count]) => (
              <div key={mode} className="border border-velta-border rounded-md p-4">
                <div className="text-xs font-mono uppercase tracking-widest text-velta-mute mb-2">{mode}</div>
                <div className="font-mono text-3xl font-bold">{count}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
